import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import pricingConfig from "../config/pricingPage.json";
import pricingHeroShot from "../assets/prices/Speak Your Menu Pricing Hero.png";
import "../styles/homepage-redesign.css";
import "../styles/pricing-page.css";

const APP_SIGNUP_ENV_URL =
  process.env.REACT_APP_APP_SIGNUP_URL ||
  process.env.APP_SIGNUP_URL ||
  process.env.NEXT_PUBLIC_APP_URL ||
  process.env.VITE_APP_URL;

// TODO: Move this fallback into shared env/config once the marketing site has a central app URL config.
const APP_SIGNUP_BASE = APP_SIGNUP_ENV_URL || "https://app.speakyourmenu.com/signup";
const START_FREE_HREF = APP_SIGNUP_BASE;
const CONTACT_HREF = "/contact";

const PRICING_CONSTANTS = Object.freeze({
  free: 0,
  singleLocation: 79.99,
  multiLocationBase: 129.99,
  multiLocationAdditional: 50,
  multiLocationMinimum: 2,
  multiLocationMaximum: 6,
  multiLocationDefault: 2,
  enterpriseThreshold: 7,
});

const HERO_BADGES = ["Unlimited users", "Web + iOS + Android", "No credit card for Free"];

const COMPARISON_STATUS = Object.freeze({
  included: "included",
  unavailable: "unavailable",
});

const COMPARISON_ICON_LABELS = Object.freeze({
  [COMPARISON_STATUS.included]: "Included",
  [COMPARISON_STATUS.unavailable]: "Not included in this plan",
});

const PLAN_CONTENT = {
  free: {
    chip: "Free",
    name: "Free",
    bestFor: "Best for trying Speak Your Menu with your current menu before you upgrade.",
    bullets: [
      "Unlimited user invites for one restaurant account",
      "Menu upload and editing tools",
      "Starter access to Food and Wine lessons",
      "Mobile and web access for managers and staff",
      "No credit card required to get started",
    ],
    ctaLabel: "Start free",
    ctaHref: START_FREE_HREF,
    tone: "neutral",
  },
  single: {
    chip: "Most common",
    name: "Single Location",
    bestFor: "Best for one restaurant that wants the full training workflow live right away.",
    bullets: [
      "Full Food and Wine training access",
      "Manager dashboard with completion tracking",
      "Menu upload, edits, and lesson generation",
      "Allergen and substitution training included",
      "Unlimited users with no per-employee fee",
    ],
    ctaLabel: "Choose Single Location",
    ctaHref: START_FREE_HREF,
    tone: "primary",
  },
  multi: {
    chip: "For groups",
    name: "Multi-Location",
    bestFor: "Best for 2 to 6 locations that need shared standards and centralized visibility.",
    bullets: [
      "Everything in Single Location",
      "Cross-location oversight for leaders and operators",
      "Shared reporting across restaurants",
      "Consistent allergen and substitution training across teams",
      "Cleaner rollout for new locations and menu updates",
    ],
    ctaLabel: "Choose Multi-Location",
    ctaHref: START_FREE_HREF,
    tone: "featured",
  },
  enterprise: {
    chip: "Custom",
    name: "Enterprise",
    bestFor: "Best for 7+ locations or teams that need procurement and rollout coordination.",
    bullets: [
      "Centralized leadership visibility across the organization",
      "Rollout planning for multiple regions or brands",
      "Priority onboarding coordination",
      "Flexible procurement and billing conversations",
      "Contact-led planning for complex account needs",
    ],
    ctaLabel: "Contact us",
    ctaHref: CONTACT_HREF,
    tone: "enterprise",
  },
};

const COMPARISON_ROWS = [
  {
    feature: "Unlimited users",
    values: [COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included],
  },
  {
    feature: "Web + iOS + Android access",
    values: [COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included],
  },
  {
    feature: "Menu upload + editing",
    values: [COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included],
  },
  {
    feature: "Manager dashboard",
    values: [COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included],
  },
  {
    feature: "Food basics lessons",
    values: [COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included],
  },
  {
    feature: "Wine basics lessons",
    values: [COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included],
  },
  {
    feature: "Allergen + substitution training (advanced food)",
    values: [COMPARISON_STATUS.unavailable, COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included],
  },
  {
    feature: "Wine styles & regions (advanced wine)",
    values: [COMPARISON_STATUS.unavailable, COMPARISON_STATUS.included, COMPARISON_STATUS.included, COMPARISON_STATUS.included],
  },
  {
    feature: "Add multiple restaurant locations (2+)",
    values: [COMPARISON_STATUS.unavailable, COMPARISON_STATUS.unavailable, COMPARISON_STATUS.included, COMPARISON_STATUS.included],
  },
  {
    feature: "Multi-location oversight (rollup view)",
    values: [COMPARISON_STATUS.unavailable, COMPARISON_STATUS.unavailable, COMPARISON_STATUS.included, COMPARISON_STATUS.included],
  },
  {
    feature: "Centralized location reporting",
    values: [COMPARISON_STATUS.unavailable, COMPARISON_STATUS.unavailable, COMPARISON_STATUS.included, COMPARISON_STATUS.included],
  },
];

const FAQ_ITEMS = [
  {
    question: "Do I need a credit card to start free?",
    answer: "No. The Free plan is available without a credit card.",
  },
  {
    question: "Can I cancel anytime?",
    answer: "Yes. Pricing is monthly and you can change plans or cancel without an annual commitment.",
  },
  {
    question: "What counts as a location?",
    answer: "A location is one restaurant operating as its own training site. If your group runs multiple restaurants, each restaurant counts as one location.",
  },
  {
    question: "Can I switch plans later?",
    answer: "Yes. Many teams start on Free, move to Single Location, and upgrade to Multi-Location as they grow.",
  },
  {
    question: "How does multi-location billing work?",
    answer: "Multi-Location pricing is $129.99 per month for the first two locations, then adds $50 per month for each additional location up to six total locations.",
  },
  {
    question: "Do you offer onboarding help?",
    answer: "Yes. Single and Multi-Location teams can reach out for setup guidance, and Enterprise accounts are handled through a contact-led planning process.",
  },
  {
    question: "Do you offer annual billing?",
    answer: "Not yet.",
  },
  {
    question: "What support is included?",
    answer: "All plans can reach Speak Your Menu through existing support and contact channels. Larger rollouts can be discussed through the Contact route.",
  },
];

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function buildSignupHref(plan, extraParams = {}) {
  const signupUrl = new URL(APP_SIGNUP_BASE);
  const params = new URLSearchParams(signupUrl.search);

  params.set("plan", plan);

  Object.entries(extraParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });

  signupUrl.search = params.toString();
  return signupUrl.toString();
}

function ActionLink({ href, className, children, ...rest }) {
  if (href.startsWith("/")) {
    return (
      <Link to={href} className={className} {...rest}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} className={className} {...rest}>
      {children}
    </a>
  );
}

function PlanCard({ children, tone = "neutral" }) {
  return <article className={`pricing-plan-card pricing-plan-card-${tone}`}>{children}</article>;
}

function ComparisonStatusIcon({ status }) {
  const label = COMPARISON_ICON_LABELS[status] || "Plan comparison status";

  if (status === COMPARISON_STATUS.included) {
    return (
      <span className="pricing-comparison-status pricing-comparison-status-included" role="img" aria-label={label} title={label}>
        <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M7.8 13.9 4.6 10.7l-1.4 1.4 4.6 4.6L16.8 7.7l-1.4-1.4z" />
        </svg>
      </span>
    );
  }

  return (
    <span className="pricing-comparison-status pricing-comparison-status-unavailable" role="img" aria-label={label} title={label}>
      <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <path d="M5 10h10" />
      </svg>
    </span>
  );
}

function renderComparisonValue(value) {
  return <ComparisonStatusIcon status={value} />;
}

export default function PricingPage() {
  const cfg = pricingConfig || {};
  const canonicalUrl = "https://speakyourmenu.com/pricing";
  const defaultShareImage = "https://speakyourmenu.com/SpeakYourMenu_Logo.png";
  const seo = {
    title: cfg.seo?.title || "Pricing | Speak Your Menu",
    description:
      cfg.seo?.metaDescription ||
      "Simple monthly pricing for restaurants using Speak Your Menu, from a free plan to multi-location and enterprise options.",
    image: cfg.seo?.shareImage || defaultShareImage,
    url: canonicalUrl,
  };

  const [locationCount, setLocationCount] = useState(PRICING_CONSTANTS.multiLocationDefault);

  const freePlanHref = useMemo(() => buildSignupHref("free"), []);
  const singlePlanHref = useMemo(() => buildSignupHref("single"), []);
  const multiPlanHref = useMemo(() => buildSignupHref("multi", { locations: locationCount }), [locationCount]);

  const multiLocationTotal = useMemo(
    () => PRICING_CONSTANTS.multiLocationBase + (locationCount - PRICING_CONSTANTS.multiLocationMinimum) * PRICING_CONSTANTS.multiLocationAdditional,
    [locationCount]
  );

  const offersSchema = [
    {
      name: "Free",
      price: String(PRICING_CONSTANTS.free),
      description: PLAN_CONTENT.free.bestFor,
      href: freePlanHref,
    },
    {
      name: "Single Location",
      price: String(PRICING_CONSTANTS.singleLocation),
      description: PLAN_CONTENT.single.bestFor,
      href: singlePlanHref,
    },
    {
      name: "Multi-Location",
      price: String(PRICING_CONSTANTS.multiLocationBase),
      description: PLAN_CONTENT.multi.bestFor,
      href: multiPlanHref,
    },
  ].map((plan) => ({
    "@type": "Offer",
    name: plan.name,
    price: plan.price,
    priceCurrency: "USD",
    description: plan.description,
    availability: "https://schema.org/InStock",
    url: plan.href,
  }));

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
      { "@type": "ListItem", position: 2, name: "Pricing", item: canonicalUrl },
    ],
  };

  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Speak Your Menu",
    description: seo.description,
    url: canonicalUrl,
    image: seo.image,
    brand: { "@type": "Brand", name: "Speak Your Menu" },
    offers: offersSchema,
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        {/* Open Graph */}
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seo.url} />
        <meta property="og:image" content={seo.image} />
        <meta property="og:site_name" content="Speak Your Menu" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={seo.image} />
        <meta name="twitter:site" content="@speakyourmenu" />
        <link rel="canonical" href={seo.url} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(pricingSchema)}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="pricing-page">
        <section className="homepage-section homepage-hero-section pricing-hero-section">
          <div className="homepage-hero-bg pricing-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell homepage-hero-grid pricing-hero-grid">
            <div className="homepage-hero-copy pricing-hero-copy">
              <h1 className="homepage-hero-title pricing-hero-title">Simple, transparent pricing for restaurants.</h1>
              <p className="homepage-hero-subhead pricing-hero-subhead">Start free. Upgrade when you&apos;re ready. Cancel anytime.</p>
              <div className="pricing-badge-row" aria-label="Pricing highlights">
                {HERO_BADGES.map((badge) => (
                  <span key={badge} className="pricing-mini-chip">
                    {badge}
                  </span>
                ))}
              </div>
              <div className="pricing-hero-actions features-hero-actions">
                <ActionLink
                  href={START_FREE_HREF}
                  className="homepage-primary-cta"
                  data-cta-name="start_free"
                  data-cta-location="pricing_hero"
                >
                  Start free
                </ActionLink>
                <ActionLink href={CONTACT_HREF} className="pricing-secondary-cta">
                  Contact
                </ActionLink>
              </div>
            </div>

            <div className="pricing-shot-shell">
              <div className="pricing-shot-frame">
                <img
                  src={pricingHeroShot}
                  alt="Speak Your Menu pricing and product overview"
                  className="pricing-shot-image"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="homepage-section pricing-plans-section pricing-band-soft">
          <div className="homepage-shell">
            <div className="pricing-section-heading pricing-section-heading-tight">
              <span className="homepage-label-chip">PLANS</span>
              <h2 className="homepage-section-title pricing-section-title">Choose the plan that fits your restaurant today.</h2>
              <p className="homepage-section-body pricing-section-body">
                Monthly pricing only. Start free, upgrade when you need the full workflow, and contact us when your rollout gets more complex.
              </p>
            </div>

            <div className="pricing-plan-grid">
              <PlanCard tone={PLAN_CONTENT.free.tone}>
                <div className="pricing-plan-topline">
                  <span className="pricing-plan-chip">{PLAN_CONTENT.free.chip}</span>
                </div>
                <h3 className="pricing-plan-title">{PLAN_CONTENT.free.name}</h3>
                <div className="pricing-plan-price-block">
                  <span className="pricing-plan-price">{formatCurrency(PRICING_CONSTANTS.free)}</span>
                  <span className="pricing-plan-period">/ month</span>
                </div>
                <p className="pricing-plan-bestfor">{PLAN_CONTENT.free.bestFor}</p>
                <ul className="pricing-plan-list">
                  {PLAN_CONTENT.free.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
                <ActionLink
                  href={freePlanHref}
                  className="homepage-primary-cta pricing-plan-cta pricing-plan-cta-neutral"
                  data-cta-name="start_free"
                  data-cta-location="pricing_plan_free"
                >
                  {PLAN_CONTENT.free.ctaLabel}
                </ActionLink>
              </PlanCard>

              <PlanCard tone={PLAN_CONTENT.single.tone}>
                <div className="pricing-plan-topline">
                  <span className="pricing-plan-chip pricing-plan-chip-accent">{PLAN_CONTENT.single.chip}</span>
                </div>
                <h3 className="pricing-plan-title">{PLAN_CONTENT.single.name}</h3>
                <div className="pricing-plan-price-block">
                  <span className="pricing-plan-price">{formatCurrency(PRICING_CONSTANTS.singleLocation)}</span>
                  <span className="pricing-plan-period">/ month</span>
                </div>
                <p className="pricing-plan-bestfor">{PLAN_CONTENT.single.bestFor}</p>
                <ul className="pricing-plan-list">
                  {PLAN_CONTENT.single.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
                <ActionLink
                  href={singlePlanHref}
                  className="homepage-primary-cta pricing-plan-cta"
                  data-cta-name="start_free"
                  data-cta-location="pricing_plan_single"
                >
                  {PLAN_CONTENT.single.ctaLabel}
                </ActionLink>
              </PlanCard>

              <PlanCard tone={PLAN_CONTENT.multi.tone}>
                <div className="pricing-plan-topline">
                  <span className="pricing-plan-chip pricing-plan-chip-featured">{PLAN_CONTENT.multi.chip}</span>
                </div>
                <h3 className="pricing-plan-title">{PLAN_CONTENT.multi.name}</h3>
                <div className="pricing-plan-price-block pricing-plan-price-block-stack">
                  <span className="pricing-plan-price-note">{formatCurrency(PRICING_CONSTANTS.multiLocationBase)} for the first 2 locations + {formatCurrency(PRICING_CONSTANTS.multiLocationAdditional)} each additional</span>
                </div>
                <p className="pricing-plan-bestfor">{PLAN_CONTENT.multi.bestFor}</p>

                <div className="pricing-location-calculator" aria-label="Multi-location pricing calculator">
                  <div className="pricing-location-panel">
                    <span className="pricing-location-label">Locations</span>
                    <div className="pricing-stepper" role="group" aria-label="Location count selector">
                      <button
                        type="button"
                        className="pricing-stepper-button"
                        onClick={() => setLocationCount((count) => clamp(count - 1, PRICING_CONSTANTS.multiLocationMinimum, PRICING_CONSTANTS.multiLocationMaximum))}
                        aria-label="Decrease location count"
                      >
                        -
                      </button>
                      <span className="pricing-stepper-value" aria-live="polite">{locationCount}</span>
                      <button
                        type="button"
                        className="pricing-stepper-button"
                        onClick={() => setLocationCount((count) => clamp(count + 1, PRICING_CONSTANTS.multiLocationMinimum, PRICING_CONSTANTS.multiLocationMaximum))}
                        aria-label="Increase location count"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="pricing-total-panel">
                    <span className="pricing-estimate-label">Estimated total</span>
                    <p className="pricing-estimate-total">{formatCurrency(multiLocationTotal)} / month</p>
                  </div>
                </div>
                <ul className="pricing-plan-list pricing-plan-list-tight">
                  {PLAN_CONTENT.multi.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
                <ActionLink
                  href={multiPlanHref}
                  className="homepage-primary-cta pricing-plan-cta"
                  data-cta-name="start_free"
                  data-cta-location="pricing_plan_multi"
                >
                  {PLAN_CONTENT.multi.ctaLabel}
                </ActionLink>
              </PlanCard>

              <PlanCard tone={PLAN_CONTENT.enterprise.tone}>
                <div className="pricing-plan-topline">
                  <span className="pricing-plan-chip pricing-plan-chip-muted">{PLAN_CONTENT.enterprise.chip}</span>
                </div>
                <h3 className="pricing-plan-title">{PLAN_CONTENT.enterprise.name}</h3>
                <div className="pricing-plan-price-block pricing-plan-price-block-stack">
                  <span className="pricing-plan-price pricing-plan-price-custom">Custom</span>
                  <span className="pricing-plan-subtitle">{PRICING_CONSTANTS.enterpriseThreshold}+ locations or enterprise needs</span>
                </div>
                <p className="pricing-plan-bestfor">{PLAN_CONTENT.enterprise.bestFor}</p>
                <ul className="pricing-plan-list">
                  {PLAN_CONTENT.enterprise.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
                <ActionLink href={PLAN_CONTENT.enterprise.ctaHref} className="pricing-secondary-cta pricing-plan-cta pricing-plan-cta-secondary">
                  {PLAN_CONTENT.enterprise.ctaLabel}
                </ActionLink>
              </PlanCard>
            </div>
          </div>
        </section>

        <section className="homepage-section pricing-comparison-section">
          <div className="homepage-shell">
            <div className="pricing-section-heading">
              <span className="homepage-label-chip">COMPARE PLANS</span>
              <h2 className="homepage-section-title pricing-section-title">A clearer view of what changes as you grow.</h2>
            </div>
            <div className="pricing-table-shell">
              <div className="pricing-table-scroll">
                <table className="pricing-table">
                  <colgroup>
                    <col className="pricing-table-feature-column" />
                    <col className="pricing-table-plan-column" />
                    <col className="pricing-table-plan-column" />
                    <col className="pricing-table-plan-column" />
                    <col className="pricing-table-plan-column" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col">Feature</th>
                      <th scope="col">Free</th>
                      <th scope="col">Single Location</th>
                      <th scope="col">Multi-Location</th>
                      <th scope="col">Enterprise</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARISON_ROWS.map((row) => (
                      <tr key={row.feature}>
                        <th scope="row">{row.feature}</th>
                        {row.values.map((value, index) => (
                          <td key={`${row.feature}-${index}`}>
                            {renderComparisonValue(value)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="pricing-comparison-helper">Free includes basics. Paid plans unlock advanced training.</p>
          </div>
        </section>

        <section className="homepage-section pricing-faq-section pricing-band-soft">
          <div className="homepage-shell">
            <div className="pricing-section-heading">
              <span className="homepage-label-chip">PRICING FAQ</span>
              <h2 className="homepage-section-title pricing-section-title">Answers to the questions buyers usually ask before they start.</h2>
            </div>

            <div className="pricing-faq-list">
              {FAQ_ITEMS.map((item, index) => (
                <details key={item.question} className="pricing-faq-item" open={index === 0}>
                  <summary className="pricing-faq-summary">{item.question}</summary>
                  <div className="pricing-faq-answer">
                    <p>{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section pricing-final-section">
          <div className="homepage-shell">
            <div className="pricing-final-band">
              <div>
                <span className="homepage-label-chip">NEXT STEP</span>
                <h2 className="homepage-section-title pricing-final-title">Ready to try Speak Your Menu?</h2>
              </div>
              <div className="pricing-final-actions">
                <ActionLink
                  href={START_FREE_HREF}
                  className="homepage-primary-cta"
                  data-cta-name="start_free"
                  data-cta-location="pricing_final"
                >
                  Start free
                </ActionLink>
                <ActionLink href={CONTACT_HREF} className="pricing-secondary-cta">
                  Contact
                </ActionLink>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
